import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import type { PrescriptionAnalysis, User } from '../types';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function removeUndefinedValues<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined)
      .map((item) => removeUndefinedValues(item)) as T;
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce<Record<string, unknown>>((clean, [key, item]) => {
      if (item !== undefined) {
        clean[key] = removeUndefinedValues(item);
      }
      return clean;
    }, {}) as T;
  }

  return value;
}

export async function savePrescription(
  prescription: Omit<PrescriptionAnalysis, 'createdAt'>
): Promise<void> {
  try {
    const prescriptionsRef = collection(db, 'prescriptions');
    const docRef = doc(prescriptionsRef, prescription.prescriptionId);

    await setDoc(docRef, removeUndefinedValues({
      ...prescription,
      createdAt: serverTimestamp(),
    }));
  } catch (error: any) {
    // Provide helpful error messages
    if (error.code === 'permission-denied') {
      throw new Error('Database access denied. Please check Firestore security rules.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firebase is currently unavailable. Please check your internet connection.');
    } else if (error.message?.includes('CORS')) {
      throw new Error('CORS error. Firebase requests are being blocked.');
    }
    
    throw error;
  }
}

export async function getPrescription(
  prescriptionId: string
): Promise<PrescriptionAnalysis | null> {
  const docRef = doc(db, 'prescriptions', prescriptionId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return docSnap.data() as PrescriptionAnalysis;
}

export async function getUserPrescriptions(
  userId: string
): Promise<PrescriptionAnalysis[]> {
  const q = query(
    collection(db, 'prescriptions'),
    where('userId', '==', userId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => doc.data() as PrescriptionAnalysis);
}

export async function updatePrescriptionStatus(
  prescriptionId: string,
  status: 'processing' | 'completed' | 'failed',
  updates?: Partial<PrescriptionAnalysis>
): Promise<void> {
  const docRef = doc(db, 'prescriptions', prescriptionId);
  await updateDoc(docRef, removeUndefinedValues({
    status,
    ...updates,
  }));
}

export async function deletePrescription(prescriptionId: string): Promise<void> {
  const docRef = doc(db, 'prescriptions', prescriptionId);
  await deleteDoc(docRef);
}

export async function createUser(user: User): Promise<void> {
  const usersRef = collection(db, 'users');
  const docRef = doc(usersRef, user.uid);

  await setDoc(docRef, {
    ...user,
    createdAt: serverTimestamp(),
  });
}

export async function getUser(userId: string): Promise<User | null> {
  const docRef = doc(db, 'users', userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return docSnap.data() as User;
}

export async function updateUser(
  userId: string,
  updates: Partial<User>
): Promise<void> {
  const docRef = doc(db, 'users', userId);
  await updateDoc(docRef, removeUndefinedValues(updates));
}

export async function uploadPrescriptionImage(
  userId: string,
  prescriptionId: string,
  imageFile: File
): Promise<string> {
  const fileName = `prescriptions/${userId}/${prescriptionId}/${Date.now()}_${imageFile.name}`;
  const storageRef = ref(storage, fileName);

  await uploadBytes(storageRef, imageFile);
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
}

export async function uploadAudioFile(
  userId: string,
  prescriptionId: string,
  audioBlob: Blob
): Promise<string> {
  const fileName = `audio/${userId}/${prescriptionId}/${Date.now()}.mp3`;
  const storageRef = ref(storage, fileName);

  await uploadBytes(storageRef, audioBlob);
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
}

export async function uploadQRCode(
  userId: string,
  prescriptionId: string,
  qrCodeBlob: Blob
): Promise<string> {
  const fileName = `qr-codes/${userId}/${prescriptionId}/${Date.now()}.png`;
  const storageRef = ref(storage, fileName);

  await uploadBytes(storageRef, qrCodeBlob);
  const downloadURL = await getDownloadURL(storageRef);

  return downloadURL;
}

export async function deleteStorageFile(fileUrl: string): Promise<void> {
  try {
    const storageRef = ref(storage, fileUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
  }
}
